import { format } from 'date-fns';
import { type TFunction } from 'react-i18next';

import { type ChatHistoricalRecord, type LegacyChatRecordData, type ChatRecordData } from '../types';

interface NormalizedChatData {
    sentAt: string;
    to: string;
    from: string;
    text: string;
    hidden: string;
}

const isLegacyFormat = (data: LegacyChatRecordData | ChatRecordData): boolean => {
    if ('text' in data && typeof data.text === 'string') {
        return true;
    }
    return false;
};

const formatChannelName = (
    record: ChatHistoricalRecord,
    legacy: boolean,
    t: TFunction<'admin_settings', undefined>
): string => {
    if (record?.type === 'breakout') {
        if (legacy) {
            return (record.data as LegacyChatRecordData).channel;
        }

        return (record.data as ChatRecordData).breakout_id || '-';
    }

    if (record?.type === 'public') {
        return t('classes.everyone');
    }

    return '-';
};

const formatName = (data: ChatRecordData, t: TFunction<'admin_settings', undefined>) => {
    if (!data.from?.is_verified) {
        return t('classes.name-unverified', { name: data.from?.name });
    }

    return data.from.name;
};

export const normalizeChatRecord = (
    record: ChatHistoricalRecord,
    t: TFunction<'admin_settings', undefined>
): NormalizedChatData => {
    const formattedDateAndTime = format(new Date(record.timestamp * 1000), 'yyyy-MM-dd hh:mm:ss a');

    if (isLegacyFormat(record.data)) {
        const { text, from } = record.data as LegacyChatRecordData;
        const username = from?.userName || '-';

        return {
            sentAt: formattedDateAndTime,
            to: formatChannelName(record, true, t),
            from: username,
            text,
            hidden: record.is_hidden ? t('classes.yes') : t('classes.no')
        };
    }

    const { content } = record.data as ChatRecordData;

    return {
        sentAt: formattedDateAndTime,
        to: formatChannelName(record, false, t),
        from: formatName(record.data as ChatRecordData, t),
        text: content,
        hidden: record.is_hidden ? t('classes.yes') : t('classes.no')
    };
};
